export interface PaywallSettings {
  style: PaywallStyles;
  product: Product;
  products?: Product[];
  productIds?: string[];
  currencies: Currency[];
  paymentMethods: PaymentMethod[];
  billingPeriods: BillingPeriod[];
  termsAndConditionLink: string;
  termsAndConditionLinkText: string;
  discount: number;
  thankYouData: ThankYouData;
  chargePlanData: ChargePlanData[];
  contactSalesData?: ContactSalesData;
  checkoutFields: CheckoutFields;
  /** Purchases skip Stripe: the buyer gets a "Simulate payment" page instead of the Stripe checkout. */
  testMode?: boolean;
}

/** The payment a test mode checkout asks the buyer to simulate. Amount is subtotal plus tax, as Stripe would charge. */
export interface SimulatedPayment {
  amount: number;
  subtotal: number;
  tax: number;
  currencyCode: string;
}

export interface CheckoutFields {
  organizationNumberEnabled: boolean;
  vatNumberEnabled: boolean;
  viesValidationEnabled: boolean;
}

/** CountryMismatch: another EU member state's VAT number than the selected country's. The purchase is rejected. */
export type VatValidationStatus = 'Valid' | 'Invalid' | 'Unverifiable' | 'CountryMismatch';

export interface ValidateVatResponse {
  isValid: boolean;
  status: VatValidationStatus;
}

export enum BillingPeriod {
  Monthly = 0,
  Quarterly = 1,
  Annual = 2,
  EndOfTerm = 3,
  Biannual = 4
}

export interface ThankYouData {
  thankYouText: string;
  thankYouRedirectUrl: string;
  useThankYouRedirect: boolean;
}

export interface ChargePlanData {
  chargePlanId: string;
  subTitle: string;
  includesTitle: string;
  includesSubTitle: string[];
  quantityStep?: number;
}

export interface ContactSalesData {
  enabled?: boolean;
  title?: string;
  subTitle?: string;
  salesEmail?: string;
  slaNote?: string;
  includesTitle?: string;
  includesSubTitle?: string[];
  /** Ordered form fields configured by the tenant. Absent means the legacy email/company/note form. */
  fields?: ContactSalesField[];
}

export type ContactSalesFieldKey = 'email' | 'firstName' | 'lastName' | 'company' | 'phone' | 'seats' | 'note';

export interface ContactSalesField {
  key: ContactSalesFieldKey;
  label?: string;
  enabled: boolean;
  required: boolean;
}

/**
 * Page context for HubSpot tracking. Deliberately holds no card selection (plan, seats, currency, billing
 * period): Contact Sales is for buyers none of the cards fit, so the lead only carries what they entered.
 */
export interface ContactSalesLeadContext {
  pageUri?: string;
  pageName?: string;
  /** HubSpot tracking cookie (hubspotutk) when present on the host page. */
  hutk?: string;
}

export interface ContactSalesLead {
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  phone?: string;
  seats?: number;
  note?: string;
  context: ContactSalesLeadContext;
}

export interface PaymentMethod {
  id: string;
  name: string;
}

export interface Currency {
  id: string;
  description: string;
  code: string;
  currencySymbol: string;
  value: string;
}

export enum ProductType {
  Simple = 0,
  MultipleCharges = 1,
  MultipleChargePlans = 2,
  Full = 3
}

export enum ChargeType {
  OneOff = 0,
  Recurring = 1,
  Usage = 2,
  Measured = 3
}

export enum PricePeriod {
  Monthly = 0,
  Quarterly = 1,
  Annual = 2
}

export interface Product {
  id: string;
  name: string;
  productNumber: string;
  imageUrl: string;
  /** Only Full products can be added to new paywalls; older paywalls may still carry other types. */
  productType?: ProductType | keyof typeof ProductType;
  chargePlans: ChargePlan[];
}

export interface ChargePlan {
  chargePlanNumber: string;
  charges: Charge[];
  name: string;
  description: string;
  includedFeaturesText: string;
  id: string;
}

export interface Charge {
  id: string;
  name: string;
  description: string;
  chargeNumber: string;
  /** Number from the API; older backends omit it, which means Recurring. */
  chargeType?: ChargeType | keyof typeof ChargeType;
  /** The period the detail prices are expressed in; older backends omit it, which means Monthly. */
  pricePeriod?: PricePeriod | keyof typeof PricePeriod;
  chargeDetails: ChargeDetails[];
  billingPeriod?: BillingPeriod;
  model: number;
  defaultQuantity: number;
}

export interface ChargeDetails {
  id: string;
  price: number;
  priceBase: number;
  priceCurrencyCode: string;
  tier: number;
  toQuantity: number;
  fromQuantity: number;
}

export interface PaywallStyles {
  // Colors
  baseColor: string
  accentColor: string
  backgroundColor: string
  lightGreyColor: string;
  darkGreyColor: string;

  // Fonts
  primaryFont: string;
  baseFontColor: string;

  // Others
  borderRadius: string;
  boxShadow: string;
}

export interface ChargePlanPurchase {
  chargePlanId: string;
  quantity: number;
}

export interface PurchaseSubscriptionModel {
  currencyId: string;
  companyName: string;
  organizationNumber: string;
  street: string;
  street2: string;
  zip: string;
  city: string;
  state: string;
  countryId: string;
  productId: string;
  chargePlans: ChargePlanPurchase[];
  billingPeriod: number;
}